import { useFacilityContext } from '@aspendental/shared-components-web';
import { BinaryChoice, BinaryChoiceSchema, DateTextInput, Form, MaskedPhoneNumberInput, MaskedPhoneNumberInputSchema, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import { Alert, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import NeedsBasedQuestions from '@src/components/NeedsBasedQuestions';
import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { AppointmentErrorMessages, DataLayerStepNames } from '@src/constants';
import { useScrollToFirstError } from '@src/hooks/useScrollToFirstError';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { schedulerLeadEvent } from '@src/services/schedulerLeadEvent';
import { PatientInformationSchema, useAppStore, useFormSectionStore, useGlobalUXStore, useGuarantorInformationFormStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import { ageEligibleForProcedure, guarantorInformationNeeded } from '@src/utils/business-logic';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { dateInvalid } from '@src/utils/date-parser';
import { formatPhoneNumber } from '@src/utils/format-phone-number';
import { trackFormValidationError } from '@src/utils/form-analytics';
import { unacceptableInputs } from '@src/utils/offensive-words';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';

/**
 * Component: FullPatientInformationForm
 *
 * This form is used exclusively for the Motto flow, and manages guarantor and age restrictions
 * differently than standard flow.
 */
const FullPatientInformationForm = ({
  formId,
  shouldUseMottoConfig = false
}: {
  formId: string;
  shouldUseMottoConfig?: boolean;
}) => {
  const completeActiveNode = useFormSectionStore(store => store.completeActiveNode);
  const setGuarantorInfoNeeded = useFormSectionStore(store => store.setGuarantorInfoNeeded);
  const setGuarantorRequired = useGuarantorInformationFormStore(store => store.setRequired);
  const patientAge = usePatientInformationFormStore(store => store.patientAge);
  const {
    mottoFlow
  } = useAppStore(store => store);
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const webFacilityDetails = useFacilityContext();
  const {
    t
  } = useTranslation('patientInformationForm');
  const router = useRouter();
  const {
    locale
  } = router;
  const openDialogWithError = useGlobalUXStore(store => store.openDialogWithError);
  const {
    email,
    setEmail,
    phonenumber,
    setPhonenumber,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
    hasInsurance,
    setHasInsurance
  } = usePatientInformationFormStore(store => store);
  const {
    hasNeedsBasedQuestions,
    needsBasedAnswers,
    setNeedsBasedAnswers
  } = useNeedsBasedQuestionsStore(store => store);
  const [hasError, setHasError] = useState(false);
  const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
  const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');
  const defaultValues = {
    firstName,
    lastName,
    dateOfBirth: initialValueDateOfBirth,
    email,
    phonenumber: formatPhoneNumber(phonenumber) || phonenumber,
    hasInsurance
  };
  const schema = SchemaBuilder([{
    name: 'firstName',
    validation: PatientInformationSchema.firstName
  }, {
    name: 'lastName',
    validation: PatientInformationSchema.lastName
  }, {
    name: 'dateOfBirth',
    validation: PatientInformationSchema.dateOfBirth
  }, {
    name: 'email',
    validation: PatientInformationSchema.email
  }, {
    name: 'phonenumber',
    validation: MaskedPhoneNumberInputSchema.options.default
  }, {
    name: 'hasInsurance',
    validation: BinaryChoiceSchema.options.default
  }]);

  // setting the session storage key to false for the patient information step to prevent multiple data layer events
  sessionStorageHelper.setKey(DataLayerStepNames.review, 'false');
  useEffect(() => {
    //this method is added to prevent multiple data layer events from being fired in mobile view
    //pass the step name as first parameter
    fireDataLayerEvent(DataLayerStepNames.patientInformation, {
      language_preference: locale,
      brand: mottoFlow ? 'mottoaligners' : 'aspendental',
      step_name: DataLayerStepNames.patientInformation,
      reason_for_visit: reasonForVisit,
      reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      })
    });
  }, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow]);
  function isNBQFormValid(data: Record<string, string[]> | undefined) {
    for (const value of Object.values(data ?? {})) {
      if (value.length === 0) return false;
    }
    return true;
  }
  const handleSubmit = (fields: FieldValues) => {
    if (hasNeedsBasedQuestions && !isNBQFormValid(needsBasedAnswers)) {
      setHasError(true);
      return;
    } else {
      setHasError(false);
    }
    const unacceptableFields = unacceptableInputs([{
      name: 'firstName',
      value: fields.firstName
    }, {
      name: 'lastName',
      value: fields.lastName
    }]);
    if (unacceptableFields.length) {
      setOffensiveInputsFound(true);
      trackFormValidationError({
        formName: 'patient_information',
        errorType: 'offensive_input',
        fields: unacceptableFields
      });
      return;
    }
    setOffensiveInputsFound(false);
    setFirstName(fields.firstName);
    setLastName(fields.lastName);
    setDateOfBirth(fields.dateOfBirth);
    setEmail(fields.email);
    setPhonenumber(fields.phonenumber.replace(/[^\d]/g, ''));
    setHasInsurance(fields.hasInsurance);
    const age = patientAge() || 0;
    const ageOK = ageEligibleForProcedure(age, reasonForVisit as DigitalVisitReasonEnum);
    if (!ageOK) {
      openDialogWithError(AppointmentErrorMessages.onlineUnavailable);
      return;
    }
    const guarantorRequired = guarantorInformationNeeded(age);
    setGuarantorRequired(guarantorRequired);
    setGuarantorInfoNeeded(guarantorRequired);
    void schedulerLeadEvent({
      firstName: fields.firstName,
      lastName: fields.lastName,
      email: fields.email,
      dateOfBirth: fields.dateOfBirth,
      reasonForVisit,
      shouldUseMottoConfig
    });
    setTimeout(() => {
      completeActiveNode();
    }, 50);
  };
  const form = useHookForm({
    schema,
    defaultValues,
    shouldFocusError: false
  });
  useScrollToFirstError(form as any, formId);
  return <Form form={form as any} onSubmit={handleSubmit} formId={formId} data-sentry-element="Form" data-sentry-component="FullPatientInformationForm" data-sentry-source-file="FullPatientInformationForm.tsx">
			<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
					<TextInput name="firstName" label="First Name" data-test-id="patient-info-firstname" data-private data-sentry-element="TextInput" data-sentry-source-file="FullPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
					<TextInput name="lastName" label="Last Name" data-test-id="patient-info-lastname" data-private data-sentry-element="TextInput" data-sentry-source-file="FullPatientInformationForm.tsx" />
				</Grid>
				{offensiveInputsFound ? <Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('offensiveInputWarning')}</Typography>
						</Alert>
					</Grid> : null}
				<Grid item xs={12} sm={6} data-private data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
					<DateTextInput label="Date of Birth" name="dateOfBirth" autoComplete="bday" required data-sentry-element="DateTextInput" data-sentry-source-file="FullPatientInformationForm.tsx" />
				</Grid>
				<Grid item sm={6} sx={{
        display: {
          xs: 'none',
          sm: 'block'
        }
      }} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx" />
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
					<TextInput name="email" label="Email Address" enforceAlphaNumeric={false} placeholder="email@domain.com" data-private data-sentry-element="TextInput" data-sentry-source-file="FullPatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
					<MaskedPhoneNumberInput name="phonenumber" label="Mobile Phone Number" data-private data-sentry-element="MaskedPhoneNumberInput" data-sentry-source-file="FullPatientInformationForm.tsx" />
				</Grid>
				{hasNeedsBasedQuestions ? <NeedsBasedQuestions setNeedsBasedAnswers={setNeedsBasedAnswers} needsBasedAnswers={needsBasedAnswers} hasError={hasError} /> : null}
				<Grid item xs={12} sm={7} data-sentry-element="Grid" data-sentry-source-file="FullPatientInformationForm.tsx">
					<BinaryChoice groupLabel="Do you have dental insurance you'd like to use?" name="hasInsurance" defaultSelection={defaultValues.hasInsurance} variant="bodyLargeSemiBold" color="text.primary" data-sentry-element="BinaryChoice" data-sentry-source-file="FullPatientInformationForm.tsx" />
				</Grid>
			</Grid>
		</Form>;
};
export default FullPatientInformationForm;